/**
 * 施設管理ページ（F-03）
 *
 * SYSTEM_ADMIN専用。施設の一覧表示、新規登録、編集、無効化/有効化を行う。
 */

import { useState } from 'react';
import { useApi, useMutation } from '../hooks/useApi';
import type { Facility } from '../types';

/** 施設作成/更新リクエスト */
interface FacilityFormData {
  code: string;
  name: string;
}
const FACILITY_CODE_REGEX = /^[A-Za-z0-9]+$/;
const FACILITY_CODE_MAX_LENGTH = 20;

function FacilitiesPage() {
  // データ取得
  const { data: facilities, loading, error, refetch } = useApi<Facility[]>('/api/facilities');

  // ミューテーション
  const createMutation = useMutation<Facility, FacilityFormData>('/api/facilities', 'POST');

  // UI状態
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ facility: Facility; action: 'activate' | 'deactivate' } | null>(null);

  // フォーム状態
  const [formData, setFormData] = useState<FacilityFormData>({ code: '', name: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ========================================
  // 新規登録
  // ========================================
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const normalizedCode = formData.code.trim();

    if (!normalizedCode || !formData.name.trim()) {
      setFormError('施設コードと施設名は必須です');
      return;
    }
    if (normalizedCode.length > FACILITY_CODE_MAX_LENGTH) {
      setFormError(`施設コードは${FACILITY_CODE_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    if (!FACILITY_CODE_REGEX.test(normalizedCode)) {
      setFormError('施設コードは英数字のみで入力してください');
      return;
    }

    const result = await createMutation.mutate({ ...formData, code: normalizedCode });
    if (result) {
      setShowCreateForm(false);
      setFormData({ code: '', name: '' });
      refetch();
    } else if (createMutation.error) {
      setFormError(createMutation.error);
    }
  };

  const openCreateForm = () => {
    setFormData({ code: '', name: '' });
    setFormError(null);
    createMutation.reset();
    setShowCreateForm(true);
  };

  // ========================================
  // 編集
  // ========================================
  const openEditModal = (facility: Facility) => {
    setFormData({ code: facility.code, name: facility.name });
    setFormError(null);
    setEditingFacility(facility);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFacility) return;
    setFormError(null);
    setSubmitting(true);

    if (!formData.code.trim() || !formData.name.trim()) {
      setFormError('施設コードと施設名は必須です');
      setSubmitting(false);
      return;
    }

    try {
      const { api } = await import('../services/api');
      await api.put<Facility>(`/api/facilities/${editingFacility.id}`, formData);
      setEditingFacility(null);
      setFormData({ code: '', name: '' });
      refetch();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'message' in err) {
        setFormError((err as { message: string }).message);
      } else {
        setFormError('更新に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================
  // 有効化/無効化
  // ========================================
  const handleToggleActive = async () => {
    if (!confirmAction) return;
    setSubmitting(true);

    try {
      const { api } = await import('../services/api');
      await api.patch<Facility>(
        `/api/facilities/${confirmAction.facility.id}/${confirmAction.action}`
      );
      setConfirmAction(null);
      refetch();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'message' in err) {
        setFormError((err as { message: string }).message);
      } else {
        setFormError('操作に失敗しました');
      }
      setConfirmAction(null);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================
  // ローディング
  // ========================================
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // エラー
  // ========================================
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-red-800 font-semibold mb-2">エラーが発生しました</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          再試行
        </button>
      </div>
    );
  }

  // ========================================
  // メインレンダリング
  // ========================================
  return (
    <div>
      {/* ページヘッダー */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">施設管理</h1>
        <div className="flex gap-2">
          <button
            onClick={refetch}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            更新
          </button>
          <button
            onClick={openCreateForm}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + 新規施設
          </button>
        </div>
      </div>

      {/* 全体エラー表示 */}
      {formError && !showCreateForm && !editingFacility && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <p className="text-red-600 text-sm">{formError}</p>
        </div>
      )}

      {/* 新規登録フォーム */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">新規施設登録</h2>
          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  施設コード <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  maxLength={FACILITY_CODE_MAX_LENGTH}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: FAC001"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  施設名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: テスト病院"
                />
              </div>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{formError}</p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormError(null);
                }}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={createMutation.loading}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {createMutation.loading ? '登録中...' : '登録'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 施設一覧 */}
      {!facilities || facilities.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          施設が登録されていません
        </div>
      ) : (
        <div className="space-y-4">
          {facilities.map((facility) => (
            <div
              key={facility.id}
              className={`bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow ${
                !facility.isActive ? 'opacity-50' : ''
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold text-gray-800">
                      {facility.name}
                    </h2>
                    {facility.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        有効
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        無効
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">
                    施設コード: {facility.code}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 mr-2">
                    登録日: {new Date(facility.createdAt).toLocaleDateString('ja-JP')}
                  </span>
                  <button
                    onClick={() => openEditModal(facility)}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    編集
                  </button>
                  {facility.isActive ? (
                    <button
                      onClick={() => setConfirmAction({ facility, action: 'deactivate' })}
                      className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                    >
                      無効化
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmAction({ facility, action: 'activate' })}
                      className="px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors"
                    >
                      有効化
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 件数表示 */}
      {facilities && facilities.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          全 {facilities.length} 件
        </div>
      )}

      {/* 編集モーダル */}
      {editingFacility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">施設情報の編集</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    施設コード <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    施設名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <p className="text-red-600 text-sm">{formError}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setEditingFacility(null);
                    setFormError(null);
                  }}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                >
                  {submitting ? '更新中...' : '更新'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 確認ダイアログ */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {confirmAction.action === 'deactivate' ? '施設の無効化' : '施設の有効化'}
            </h2>
            <p className="text-gray-600 mb-4">
              {confirmAction.action === 'deactivate'
                ? `「${confirmAction.facility.name}」を無効化しますか？無効化すると、この施設のユーザーはシステムを利用できなくなります。`
                : `「${confirmAction.facility.name}」を有効化しますか？`}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleToggleActive}
                disabled={submitting}
                className={`px-4 py-2 text-sm text-white rounded transition-colors ${
                  confirmAction.action === 'deactivate'
                    ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                    : 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'
                }`}
              >
                {submitting
                  ? '処理中...'
                  : confirmAction.action === 'deactivate'
                    ? '無効化する'
                    : '有効化する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FacilitiesPage;
